import React from 'react';
import { Box, ButtonBase, Link, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { palette } from '../../../../theme';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import { InvoiceMonthSummaryDto } from '../../../../common/interfaces/invoicing.interface';
import { FINANCE_CONTRACTS, FINANCE_DEBTS, INVOICE_BATCH_DETAILS } from '../../../../common/constants/routes.constants';
import { formatSerbianDate } from '../../../../utilities/helper/date.helper';

export type StepState = 'done' | 'current' | 'todo' | 'attention';

interface InvoiceMonthStepsProps {
  kindergartenId: string;
  summary: InvoiceMonthSummaryDto;
  /** "oktobar 2026", for the button labels. */
  monthLabel: string;
  onGenerate: () => void;
  onIssue: () => void;
  onSend: () => void;
  onDownloadZip: () => void;
  busy?: boolean;
}

/**
 * Where the month is in the four things that happen to it: contracts ready, drafts made,
 * drafts issued, invoices sent.
 *
 * Only the step that is next carries a button; the others say what is done or what comes
 * later. Contracts never block - a child that is not ready is skipped when drafts are made -
 * so that step is a warning rather than the current one when something is missing.
 */
export const stepStates = (summary: InvoiceMonthSummaryDto): [StepState, StepState, StepState, StepState] => {
  const { invoices, contracts } = summary;
  const live = invoices.all - invoices.cancelled;
  const contractsDone = contracts.all > 0 && contracts.ready === contracts.all;
  const done = [live > 0, live > 0 && invoices.draft === 0, live > 0 && invoices.draft === 0 && invoices.sendable === 0];
  const current = done.findIndex(value => !value);
  const later = done.map((value, index): StepState => (value ? 'done' : index === current ? 'current' : 'todo'));
  return [contractsDone ? 'done' : 'attention', later[0], later[1], later[2]];
};

const CIRCLE: Record<StepState, { bg: string; fg: string }> = {
  done: { bg: 'success.main', fg: palette.common.white },
  current: { bg: 'primary.main', fg: palette.common.white },
  attention: { bg: 'warning.main', fg: palette.common.white },
  todo: { bg: 'field.border', fg: palette.common.white }
};

const Step: React.FC<{
  index: number;
  state: StepState;
  title: string;
  lines: React.ReactNode[];
  action?: React.ReactNode;
}> = ({ index, state, title, lines, action }) => (
  <Box
    component="li"
    aria-current={state === 'current' ? 'step' : undefined}
    aria-label={title}
    sx={{
      listStyle: 'none',
      borderRadius: 4,
      p: '16px 20px',
      border: 1,
      borderColor: 'line',
      ...(state === 'current' ? { borderWidth: 2, borderColor: 'primary.main', bgcolor: 'surface.tint', p: '15px 19px' } : {}),
      display: 'flex',
      flexDirection: 'column',
      gap: 1
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        aria-hidden
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          bgcolor: CIRCLE[state].bg,
          color: CIRCLE[state].fg,
          fontSize: 13,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        {state === 'done' ? <CheckIcon sx={{ fontSize: 16 }} /> : index}
      </Box>
      <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{title}</Typography>
    </Box>
    {lines.filter(Boolean).map((line, i) => (
      <Typography key={i} sx={{ fontSize: 14, color: 'text.secondary' }}>
        {line}
      </Typography>
    ))}
    {action && <Box sx={{ mt: 'auto', pt: 0.5 }}>{action}</Box>}
  </Box>
);

const StepLink: React.FC<{ to?: string; onClick?: () => void; children: React.ReactNode }> = ({ to, onClick, children }) =>
  to ? (
    <Link component={RouterLink} to={to} underline="hover" sx={{ fontSize: 14, color: 'primary.main' }}>
      {children}
    </Link>
  ) : (
    <ButtonBase onClick={onClick} sx={{ fontSize: 14, color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
      {children}
    </ButtonBase>
  );

const InvoiceMonthSteps: React.FC<InvoiceMonthStepsProps> = ({
  kindergartenId,
  summary,
  monthLabel,
  onGenerate,
  onIssue,
  onSend,
  onDownloadZip,
  busy
}) => {
  const { t } = useTranslation();
  const { invoices, contracts, lastBatch } = summary;
  const [s1, s2, s3, s4] = stepStates(summary);
  const live = invoices.all - invoices.cancelled;
  const query = `?kindergartenId=${kindergartenId}`;

  const missing = [
    contracts.number && t('invoices.steps.contracts.missingNumber', { count: contracts.number }),
    contracts.payer && t('invoices.steps.contracts.missingPayer', { count: contracts.payer }),
    contracts.price && t('invoices.steps.contracts.missingPrice', { count: contracts.price }),
    contracts.group && t('invoices.steps.contracts.missingGroup', { count: contracts.group })
  ].filter(Boolean);

  const primary = (label: string, onClick: () => void) => (
    <GenericButton buttonType={GenericButtonTypeEnum.Primary} variant="contained" label={label} onClick={onClick} disabled={busy} />
  );

  return (
    <Box
      component="ol"
      aria-label={t('invoices.steps.ariaLabel')}
      sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, m: 0, p: 0, mb: 3 }}
    >
      <Step
        index={1}
        state={s1}
        title={t('invoices.steps.contracts.title')}
        lines={[
          t('invoices.steps.contracts.ready', { ready: contracts.ready, count: contracts.all }),
          missing.length ? t('invoices.steps.contracts.missing', { what: missing.join(', ') }) : null
        ]}
        action={<StepLink to={`${FINANCE_CONTRACTS}${query}`}>{t('invoices.steps.contracts.link')}</StepLink>}
      />
      <Step
        index={2}
        state={s2}
        title={t('invoices.steps.drafts.title')}
        lines={
          s2 === 'done'
            ? [
                lastBatch
                  ? t('invoices.steps.drafts.made', {
                      count: lastBatch.createdCount + lastBatch.issuedCount,
                      skipped: lastBatch.skippedCount + lastBatch.failedCount,
                      date: formatSerbianDate(lastBatch.createdAt)
                    })
                  : t('invoices.steps.drafts.forMonth', { count: live })
              ]
            : [t('invoices.steps.drafts.todo')]
        }
        action={
          s2 === 'current'
            ? primary(t('invoices.steps.drafts.generate', { month: monthLabel }), onGenerate)
            : lastBatch && (
                <StepLink to={`${INVOICE_BATCH_DETAILS.replace(':batchId', lastBatch.id)}${query}`}>
                  {lastBatch.skippedCount + lastBatch.failedCount ? t('invoices.steps.drafts.skippedLink') : t('invoices.steps.drafts.runLink')}
                </StepLink>
              )
        }
      />
      <Step
        index={3}
        state={s3}
        title={t('invoices.steps.issue.title')}
        lines={
          s3 === 'done'
            ? [t('invoices.steps.issue.done', { issued: invoices.issued, count: live })]
            : s3 === 'current'
              ? [t('invoices.steps.issue.waiting', { count: invoices.draft })]
              : [t('invoices.steps.issue.todo')]
        }
        action={
          s3 === 'current'
            ? primary(t('invoices.steps.issue.button', { count: invoices.draft }), onIssue)
            : invoices.issued > 0 && <StepLink onClick={onDownloadZip}>{t('invoices.steps.issue.zip')}</StepLink>
        }
      />
      <Step
        index={4}
        state={s4}
        title={t('invoices.steps.send.title')}
        lines={
          s4 === 'done'
            ? [t('invoices.steps.send.done')]
            : s4 === 'current'
              ? [t('invoices.steps.send.waiting', { count: invoices.sendable })]
              : [t('invoices.steps.send.todo')]
        }
        action={
          s4 === 'current'
            ? primary(t('invoices.steps.send.button', { count: invoices.sendable }), onSend)
            : s4 === 'done' && <StepLink to={`${FINANCE_DEBTS}${query}`}>{t('invoices.steps.send.debtsLink')}</StepLink>
        }
      />
    </Box>
  );
};

export default InvoiceMonthSteps;
